import { useSyncExternalStore } from "react";
import type { SceneParcel } from "../blueprint/rules.js";
import type { Footprint } from "../footprints.js";
import { SIDEWALK_TOP, inRect, roadProfile, streetPlan } from "./streetPlan.js";
import type { StreetPlan } from "./streetPlan.js";
import { RAMP_DEPTH, cutStreets } from "./streetCuts.js";
import type { Ramp, StreetCuts } from "./streetCuts.js";
import { urbanLayout } from "./streets.js";
import type { Rect, UrbanLayout } from "./streets.js";
import { buildTerrain, sampleChannel, terrainHeight } from "./terrain.js";
import type { FlatRect, TerrainData } from "./terrain.js";

/**
 * The one ground model of the outdoor world (pure apart from a one-entry cache and a tiny store):
 * the terrain plus everything paved on top of it. Every consumer asks it for heights, so the drawn
 * ground, the walker's feet, the vehicle colliders, traffic, pedestrians, props and trees agree to
 * the centimeter: roads are crowned asphalt at y 0..CROWN, sidewalks and parcel pads (and their
 * curbs) stand SIDEWALK_TOP above, lawns sit flush with the pads, the rest is terrain.
 */

export type SurfaceKind = "road" | "sidewalk" | "lawn" | "grass";

interface Item {
  rect: Rect;
  /** Top above the terrain; a road computes its crown instead. */
  top: number;
  kind: SurfaceKind;
  road?: { alongZ: boolean; center: number; hw: number };
  /** A junction: the higher of the two crowns. */
  junction?: { zCenter: number; zHw: number; xCenter: number; xHw: number };
  /** A lowered curb: the slope from the curb line down into the road. */
  ramp?: Ramp;
}

export interface Ground {
  layout: UrbanLayout;
  plan: StreetPlan;
  cuts: StreetCuts;
  terrain: TerrainData;
  /** The building and object footprints the ground was built around. */
  footprints: readonly Footprint[];
  /** Height of the walkable surface at a point (terrain + pavement). */
  heightAt(x: number, z: number): number;
  /** Height of the bare terrain (under pads it is 0). */
  terrainAt(x: number, z: number): number;
  kindAt(x: number, z: number): SurfaceKind;
  /** Can grass grow here (0..1)? */
  vegetationAt(x: number, z: number): number;
}

const CELL = 24;

const toFlat = (r: Rect, grow = 0): FlatRect => ({
  minX: r.x - r.w / 2 - grow,
  maxX: r.x + r.w / 2 + grow,
  minZ: r.z - r.d / 2 - grow,
  maxZ: r.z + r.d / 2 + grow,
});

/** Builds the ground of a layout (the full layout: the tier never changes the ground). */
export function buildGround(
  parcels: readonly SceneParcel[],
  footprints: readonly Footprint[],
): Ground {
  const layout = urbanLayout(parcels, footprints, { lean: false });
  const plan = streetPlan(layout.roads);
  const cuts = cutStreets(plan, layout);
  const roadRects: Rect[] = layout.roads.map((r) =>
    r.alongZ
      ? { x: r.x, z: r.z, w: r.width, d: r.length }
      : { x: r.x, z: r.z, w: r.length, d: r.width },
  );
  const fps: Rect[] = footprints.map((f) => {
    const c = Math.abs(Math.cos(f.yaw));
    const s = Math.abs(Math.sin(f.yaw));
    return { x: f.x, z: f.z, w: f.width * c + f.depth * s, d: f.width * s + f.depth * c };
  });
  const paved: Rect[] = [
    ...layout.pads,
    ...plan.sidewalks,
    ...plan.corners,
    ...layout.paths,
    ...cuts.aprons,
  ];
  const terrain = buildTerrain({
    flat: [
      ...parcels.map((p) => p.bounds),
      ...roadRects.map((r) => toFlat(r)),
      ...fps.map((r) => toFlat(r, 4)),
    ],
    roads: roadRects.map((r) => toFlat(r)),
    paved: [...paved.map((r) => toFlat(r)), ...fps.map((r) => toFlat(r))],
    lawns: layout.lawns.map((r) => ({ ...toFlat(r), top: SIDEWALK_TOP })),
    lifts: paved.map((r) => ({ ...toFlat(r), top: SIDEWALK_TOP })),
  });

  const items: Item[] = [];
  for (const p of layout.pads) items.push({ rect: p, top: SIDEWALK_TOP, kind: "sidewalk" });
  for (const p of plan.sidewalks) items.push({ rect: p, top: SIDEWALK_TOP, kind: "sidewalk" });
  for (const p of plan.corners) items.push({ rect: p, top: SIDEWALK_TOP, kind: "sidewalk" });
  for (const p of cuts.aprons) items.push({ rect: p, top: SIDEWALK_TOP, kind: "sidewalk" });
  for (const p of layout.lawns) items.push({ rect: p, top: SIDEWALK_TOP, kind: "lawn" });
  for (const r of cuts.ramps) {
    const c = -r.side * (RAMP_DEPTH / 2);
    const rect = r.alongZ
      ? { x: r.x + c, z: r.z, w: RAMP_DEPTH, d: r.width }
      : { x: r.x, z: r.z + c, w: r.width, d: RAMP_DEPTH };
    items.push({ rect, top: 0, kind: "road", ramp: r });
  }
  for (const s of plan.segments) {
    const rect = s.alongZ
      ? { x: s.center, z: (s.from + s.to) / 2, w: s.hw * 2, d: s.to - s.from }
      : { x: (s.from + s.to) / 2, z: s.center, w: s.to - s.from, d: s.hw * 2 };
    items.push({
      rect,
      top: 0,
      kind: "road",
      road: { alongZ: s.alongZ, center: s.center, hw: s.hw },
    });
  }
  for (const j of plan.junctions) {
    items.push({
      rect: j,
      top: 0,
      kind: "road",
      junction: { zCenter: j.x, zHw: j.w / 2, xCenter: j.z, xHw: j.d / 2 },
    });
  }
  // Spatial hash of the items.
  const grid = new Map<number, Item[]>();
  const key = (cx: number, cz: number) => cx * 73856093 + cz * 19349663;
  for (const it of items) {
    const f = toFlat(it.rect);
    for (let cx = Math.floor(f.minX / CELL); cx <= Math.floor(f.maxX / CELL); cx++) {
      for (let cz = Math.floor(f.minZ / CELL); cz <= Math.floor(f.maxZ / CELL); cz++) {
        const k = key(cx, cz);
        const list = grid.get(k);
        if (list) list.push(it);
        else grid.set(k, [it]);
      }
    }
  }
  const at = (x: number, z: number): Item[] =>
    grid.get(key(Math.floor(x / CELL), Math.floor(z / CELL))) ?? [];
  const topOf = (it: Item, x: number, z: number): number => {
    if (it.road) return roadProfile((it.road.alongZ ? x : z) - it.road.center, it.road.hw);
    if (it.ramp) {
      const r = it.ramp;
      const into = ((r.alongZ ? r.x - x : r.z - z) * r.side) / RAMP_DEPTH;
      return SIDEWALK_TOP * (1 - Math.min(1, Math.max(0, into)));
    }
    if (it.junction) {
      const j = it.junction;
      return Math.max(roadProfile(x - j.zCenter, j.zHw), roadProfile(z - j.xCenter, j.xHw));
    }
    return it.top;
  };
  const rank: Record<SurfaceKind, number> = { grass: 0, road: 1, lawn: 2, sidewalk: 3 };
  return {
    layout,
    plan,
    cuts,
    terrain,
    footprints,
    terrainAt: (x, z) => terrainHeight(terrain, x, z),
    heightAt(x, z) {
      let top = -1;
      for (const it of at(x, z)) {
        if (!inRect(it.rect, x, z)) continue;
        top = Math.max(top, topOf(it, x, z));
      }
      return terrainHeight(terrain, x, z) + Math.max(0, top);
    },
    kindAt(x, z) {
      let kind: SurfaceKind = "grass";
      for (const it of at(x, z)) {
        if (inRect(it.rect, x, z) && rank[it.kind] > rank[kind]) kind = it.kind;
      }
      return kind;
    },
    vegetationAt: (x, z) => sampleChannel(terrain, 1, x, z),
  };
}

let cache: { key: string; ground: Ground } | null = null;

/** The ground of a world, cached on its parcels and footprints (one entry). */
export function groundFor(
  parcels: readonly SceneParcel[],
  footprints: readonly Footprint[],
): Ground {
  const key = JSON.stringify([
    parcels.map((p) => [p.bounds.minX, p.bounds.minZ, p.bounds.maxX, p.bounds.maxZ, p.purpose]),
    footprints.map((f) => [
      f.id,
      Math.round(f.x * 10),
      Math.round(f.z * 10),
      Math.round(f.yaw * 100),
      f.width,
      f.depth,
    ]),
  ]);
  if (cache?.key === key) return cache.ground;
  const ground = buildGround(parcels, footprints);
  cache = { key, ground };
  return ground;
}

// The ground the scene shows right now (set by `Urban`), for layers that only see their own items.
let current: Ground | null = null;
const listeners = new Set<() => void>();

export function setCurrentGround(g: Ground | null): void {
  if (g === current) return;
  current = g;
  for (const l of listeners) l();
}

export function currentGround(): Ground | null {
  return current;
}

/** Height of the walkable ground at a point in the current world (0 before a world is shown). */
export function groundHeightAt(x: number, z: number): number {
  return current ? current.heightAt(x, z) : 0;
}

export function useGround(): Ground | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
    () => null,
  );
}

export { SIDEWALK_TOP };
